import { sendCustomEvent } from '@foxtrotplatform/platform-micro-site-core';
import {
  ChangeEvent,
  useCallback,
  MouseEvent,
  useMemo,
  useState,
  useEffect,
} from 'react';
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  UseFormStateReturn,
  UseFormWatch,
} from 'react-hook-form';

import { useSignUp } from '../../../common/hooks/useSignUp';
import { SignUpStep } from '../../../common/provider/SignUpContext';
import { MoneyDTO, PlanTier } from '../../../common/api/types';
import { FormFamily, SelectedSolution } from '../types';
import {
  isAnotherFamiliesProductSelected,
  costDerivation,
} from './planUtility';

interface PlanCardProps<T extends FieldValues> {
  tier: PlanTier;
  control?: Control<T, any>;
  inputKey: Path<T>;
  openCleanSolutionModal: () => void;
  watch: UseFormWatch<T>;
}

export const PlanCard = <T extends FieldValues>({
  tier,
  control,
  inputKey,
  openCleanSolutionModal,
  watch,
}: PlanCardProps<T>) => {
  const { tierId, label, meta, components, isTrial, pricingPlanType, pricing } =
    tier;
  const { additionalSkuCost } = meta;
  const additionalSkuCostObject = useMemo(() => {
    if (additionalSkuCost) {
      return JSON.parse(additionalSkuCost) as MoneyDTO;
    }
    return undefined;
  }, [additionalSkuCost]);
  const { currentStep } = useSignUp();
  const families: FormFamily[] = watch('families' as Path<T>);
  const [selectedTierId, setSelectedTierId] = useState<string | undefined>(
    undefined
  );
  const [solutionSelected, setSelectedSolution] = useState<SelectedSolution>();

  const handleOnChange = useCallback(
    (
      formCallback: (value: SelectedSolution) => void,
      value: SelectedSolution
    ) =>
      (e: ChangeEvent<HTMLInputElement>) => {
        if (isAnotherFamiliesProductSelected(families)) {
          openCleanSolutionModal();
        } else if (e.currentTarget.checked) {
          formCallback({ item: { ...value.item, selectedTier: tierId } });
        } else {
          formCallback({ item: { ...value.item, selectedTier: undefined } });
        }
      },
    [tierId, families, openCleanSolutionModal]
  );

  useEffect(() => {
    let selectedPlanLabel = solutionSelected?.item.tiers.find(
      (tier) => tier.tierId === selectedTierId
    )?.label;
    let displayName = solutionSelected?.item.displayName.replaceAll(' ', '');
    if (displayName && selectedPlanLabel) {
      sendCustomEvent({
        category: SignUpStep[currentStep],
        action: `Selected${displayName}${selectedPlanLabel}`,
        label: `User clicked on ${solutionSelected?.item.displayName} ${selectedPlanLabel}`,
        nonInteraction: false,
        href: window.location.href,
      });
    }
  }, [selectedTierId]);

  const handleOnClick = useCallback(
    (
      formCallback: (value: SelectedSolution) => void,
      value: SelectedSolution
    ) =>
      (e: MouseEvent<HTMLButtonElement>) => {
        if (isAnotherFamiliesProductSelected(families)) {
          openCleanSolutionModal();
        } else if (
          !value?.item?.selectedTier ||
          value?.item?.selectedTier !== tierId
        ) {
          setSelectedTierId(tierId);
          setSelectedSolution(value);
          formCallback({ item: { ...value.item, selectedTier: tierId } });
        } else {
          formCallback({ item: { ...value.item, selectedTier: undefined } });
        }
      },
    [tierId, families, openCleanSolutionModal]
  );

  const renderInputRadioList = useCallback(
    ({
      field: { value, onChange, ref },
    }: {
      field: ControllerRenderProps<T, Path<T>>;
      fieldState: ControllerFieldState;
      formState: UseFormStateReturn<T>;
    }) => {
      const { displayPrice, features } = costDerivation(
        components,
        meta,
        isTrial,
        pricingPlanType,
        pricing,
        additionalSkuCostObject
      );
      return (
        <button
          className={`${
            value && value.item?.selectedTier === tierId
              ? 'bg-secondary-800'
              : 'bg-white'
          } flex flex-row items-left gap-3 rounded-md border border-danger-100 gap-y-3 py-4 pr-5 ml-8`}
          onClick={handleOnClick(onChange, value)}
          type="button"
          data-testid={`choose-${tierId}-btn`}
        >
          <div className="pl-3 pt-1">
            <input
              type="radio"
              checked={value && value.item?.selectedTier === tierId}
              value={tierId}
              onChange={handleOnChange(onChange, value)}
              className="h-4.5 w-4.5 border-slate-400 bg-white checked:border-white checked:after:bg-secondary-800 checked:bg-secondary-800 rounded-lg shadow mb-3.5"
              ref={ref}
            />
          </div>
          <div className="grow flex flex-col sm:flex-row gap-2 justify-between">
            <div className="grow flex flex-col gap-y-2">
              <div className="flex flex-row justify-between">
                <span
                  className={`${
                    value && value.item?.selectedTier === tierId
                      ? 'text-white'
                      : 'text-slate-700'
                  } text-base font-bold`}
                >
                  {label}
                </span>{' '}
              </div>
              <div
                className={`${
                  value && value.item?.selectedTier === tierId
                    ? 'text-white'
                    : 'text-black'
                } ${additionalSkuCostObject ? '' : 'pb-4'} 
                flex flex-col text-sm items-start`}
              >
                {features.map((feature, index) => (
                  <p key={index}>{feature}</p>
                ))}
              </div>
            </div>
            <div>
              <span
                className={`${
                  value && value.item?.selectedTier === tierId
                    ? 'text-white'
                    : 'text-secondary-800'
                } text-base font-bold`}
              >
                {displayPrice}
              </span>
            </div>
          </div>
        </button>
      );
    },
    [
      tierId,
      label,
      additionalSkuCostObject,
      components,
      isTrial,
      meta,
      pricingPlanType,
      handleOnClick,
      handleOnChange,
    ]
  );

  if (!control || !inputKey) {
    return null;
  }

  return (
    <Controller
      name={inputKey}
      control={control}
      render={renderInputRadioList}
    />
  );
};
